import { useEffect, useRef } from "react";

// TanStack Router(@tanstack/history)가 popstate 방향(BACK/FORWARD/GO)을 판별할 때
// 사용하는 history.state 키. 직접 pushState 할 때도 같은 모양을 유지해야
// 라우터의 이동 방향 계산이 깨지지 않는다.
const TSR_INDEX_KEY = "__TSR_index";

/** 현재 엔트리가 이미 가드용 더미인지. (StrictMode 이중 마운트 시 중복 push 방지) */
const isGuardEntry = () =>
  !!(window.history.state as Record<string, unknown> | null)?.__backGuard;

/** 현재 URL 그대로 히스토리 엔트리를 하나 더 쌓는다(= 뒤로가기 흡수용 더미). */
const pushGuardEntry = () => {
  const current = (window.history.state ?? {}) as Record<string, unknown>;
  const index = (current[TSR_INDEX_KEY] as number) ?? 0;
  window.history.pushState(
    {
      ...current,
      key: Math.random().toString(36).slice(2, 12),
      [TSR_INDEX_KEY]: index + 1,
      __backGuard: true,
    },
    "",
    window.location.href
  );
};

/**
 * 뒤로가기가 브라우저 히스토리의 "이전 항목"으로 내려가는 것을 막고,
 * 대신 앱이 지정한 화면으로 보내는 가드.
 *
 * 외부 결제창(토스)처럼 다른 도메인의 항목이 히스토리에 남아 있으면,
 * 뒤로가기 시 문서 자체가 그 페이지로 교체돼 버려서(=우리 JS가 죽어서)
 * popstate 로도 막을 수 없다. 그래서 "같은 문서의 더미 엔트리"를 항상 위에
 * 하나 얹어 두고, 뒤로가기를 그 더미가 흡수하게 만든다.
 *
 * - 마운트 시: 더미 엔트리를 하나 push
 * - popstate(뒤로가기) 시: 더미를 다시 push 한 뒤 onBack() 실행
 *   → 연타해도 외부 결제창까지 내려가지 않는다.
 */
export const useHistoryBackGuard = (onBack: () => void, enabled = true) => {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    if (!enabled) return;

    if (!isGuardEntry()) pushGuardEntry();

    const handlePopState = () => {
      // 더미가 소비됐으므로 다시 얹어 두고, 앱이 정한 화면으로 이동시킨다.
      pushGuardEntry();
      onBackRef.current();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [enabled]);
};
